import React from "react";
import { AuditLog } from "../../api/services/auditLogs/types";
import { formatDateTime } from "../../utils/formatters";

interface AuditLogTableProps {
  logs: AuditLog[];
  onViewDetails?: (log: AuditLog) => void;
}

export const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  onViewDetails,
}) => {
  const getActionBadgeClass = (action: string) => {
    switch (action) {
      case "INSERT":
        return "bg-green-100 text-green-800";
      case "UPDATE":
        return "bg-blue-100 text-blue-800";
      case "DELETE":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatChangedFields = (fields: Record<string, unknown> | undefined) => {
    if (!fields) return "-";
    const keys = Object.keys(fields);
    if (keys.length === 0) return "-";
    if (keys.length <= 3) return keys.join(", ");
    return `${keys.slice(0, 3).join(", ")} +${keys.length - 3} more`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Timestamp
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Table
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Action
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Changed Fields
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              IP Address
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {logs.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                No audit logs found
              </td>
            </tr>
          ) : (
            logs.map((log) => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formatDateTime(log.action_timestamp)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {log.table_name}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionBadgeClass(
                      log.action,
                    )}`}
                  >
                    {log.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div>
                    <div className="font-medium">
                      {log.user_name || "System"}
                    </div>
                    {log.user_email && (
                      <div className="text-xs text-gray-500">
                        {log.user_email}
                      </div>
                    )}
                    {log.user_role && (
                      <div className="text-xs text-gray-500">
                        {log.user_role}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formatChangedFields(log.changed_fields)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {log.client_ip || "-"}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onViewDetails?.(log)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
